/**
 * Session courante : jeton d'accès en mémoire et rotation (docs/06 §9).
 *
 * L'access token vit dans un store Zustand **non persisté** : ni `localStorage`,
 * ni `sessionStorage`. Il disparaît donc à la fermeture de l'onglet et n'est pas
 * exfiltrable par un XSS persistant. Le refresh token, lui, est un cookie
 * `HttpOnly` que ce code ne voit jamais.
 */
import createClient from "openapi-fetch";
import { create } from "zustand";

import { API_BASE_URL, DEFAULT_HEADERS } from "@/shared/api/config";
import { sendRequest } from "@/shared/api/http";
import type { components, paths } from "@/shared/api/generated";

export type CurrentUser = components["schemas"]["CurrentUserResponse"];
export type SessionPayload = components["schemas"]["SessionResponse"];

/**
 * `unknown` tant que la tentative de restauration silencieuse n'a pas abouti.
 *
 * Sans ce troisième état, l'application afficherait la page de connexion
 * pendant la fraction de seconde où le refresh est en vol — un clignotement à
 * chaque rechargement.
 */
export type SessionStatus = "unknown" | "authenticated" | "anonymous";

interface SessionState {
  status: SessionStatus;
  accessToken: string | null;
  user: CurrentUser | null;
  setSession: (payload: SessionPayload) => void;
  setUser: (user: CurrentUser) => void;
  clear: () => void;
}

export const useSessionStore = create<SessionState>((set) => ({
  status: "unknown",
  accessToken: null,
  user: null,
  setSession: (payload) => {
    set({ status: "authenticated", accessToken: payload.access_token, user: payload.user });
  },
  setUser: (user) => {
    set({ user });
  },
  clear: () => {
    set({ status: "anonymous", accessToken: null, user: null });
  },
}));

/**
 * Client dédié à la rotation.
 *
 * Volontairement distinct de `shared/api/client.ts` : celui-ci porte
 * l'intercepteur qui rejoue une requête après un 401 en appelant `/refresh`.
 * Rafraîchir avec ce client-là partirait en récursion infinie le jour où le
 * refresh token est lui-même expiré.
 */
const sessionApi = createClient<paths>({
  baseUrl: API_BASE_URL,
  headers: DEFAULT_HEADERS,
  // Le cookie de refresh est `SameSite=Strict` : il faut encore demander
  // explicitement son envoi en cross-origin (dev : 5173 → 8000).
  credentials: "include",
  fetch: sendRequest,
});

let inFlight: Promise<string | null> | null = null;

/**
 * Fait tourner la session. **Une seule requête concurrente** (docs/06 §9).
 *
 * Sans cette règle, deux requêtes recevant 401 en même temps déclencheraient
 * deux rotations : la seconde présenterait un jeton que la première vient de
 * révoquer, l'API y verrait un rejeu, et la session entière tomberait. Le bug
 * est intermittent, apparaît sous latence, et coûte des heures à reproduire.
 */
export function refreshSession(): Promise<string | null> {
  inFlight ??= performRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function performRefresh(): Promise<string | null> {
  const { data } = await sessionApi.POST("/api/v1/auth/refresh", {});
  if (data === undefined) {
    useSessionStore.getState().clear();
    return null;
  }
  useSessionStore.getState().setSession(data);
  return data.access_token;
}

/** Réinitialise l'état local, sans appeler l'API. */
export function clearSession(): void {
  useSessionStore.getState().clear();
}

/** Exposé pour les tests : remet le verrou de rotation à zéro entre deux cas. */
export function resetRefreshLock(): void {
  inFlight = null;
}
